import { supabase } from './supabase'
import {
  ResolvedRestaurant,
  SupabaseProduct,
  SupabaseCategory,
  SupabaseOptionGroup,
  SupabaseOption,
} from '../types/restaurant'

// ─── Domain Resolution ───────────────────────────────────────

/**
 * Normalize a hostname to match Supabase domain records.
 * Strips "www.", lowercases, and trims whitespace.
 */
export function normalizeDomain(hostname: string): string {
  return hostname
    .toLowerCase()
    .trim()
    .replace(/^www\./, '')
}

/**
 * Resolve the active restaurant profile by domain via Supabase RPC.
 * Returns the full restaurant row or null if not found / inactive.
 */
export async function resolveRestaurantByDomain(
  domain: string,
): Promise<ResolvedRestaurant | null> {
  const { data, error } = await supabase.rpc('resolve_restaurant', {
    p_domain: domain,
  })

  if (error) {
    console.error(
      '[restaurant-queries] resolveRestaurantByDomain error:',
      error,
    )
    return null
  }

  if (Array.isArray(data)) {
    return data.length > 0 ? (data[0] as ResolvedRestaurant) : null
  }

  return data ? (data as ResolvedRestaurant) : null
}

// ─── Categories ──────────────────────────────────────────────

/**
 * Fetch active categories for a restaurant, ordered by sort_order.
 */
export async function getCategoriesByRestaurant(
  restaurantId: string,
): Promise<SupabaseCategory[]> {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error(
      '[restaurant-queries] getCategoriesByRestaurant error:',
      error,
    )
    return []
  }

  return (data || []) as SupabaseCategory[]
}

// ─── Menu Items (Products) ───────────────────────────────────

/**
 * Fetch active menu items for a restaurant, ordered by sort_order.
 */
export async function getMenuItemsByRestaurant(
  restaurantId: string,
): Promise<SupabaseProduct[]> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('[restaurant-queries] getMenuItemsByRestaurant error:', error)
    return []
  }

  return (data || []) as SupabaseProduct[]
}

/**
 * Fetch active menu items filtered by category.
 */
export async function getMenuItemsByCategory(
  restaurantId: string,
  category: string,
): Promise<SupabaseProduct[]> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .eq('category', category)
    .eq('is_active', true)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('[restaurant-queries] getMenuItemsByCategory error:', error)
    return []
  }

  return (data || []) as SupabaseProduct[]
}

// ─── Option Groups & Options ─────────────────────────────────

/**
 * Fetch active option groups for a restaurant with their nested options.
 * Uses a Supabase join to fetch options in a single query.
 */
export async function getOptionGroupsByRestaurant(
  restaurantId: string,
): Promise<SupabaseOptionGroup[]> {
  const { data, error } = await supabase
    .from('option_groups')
    .select(
      `
      *,
      options:options(*)
    `,
    )
    .eq('restaurant_id', restaurantId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error(
      '[restaurant-queries] getOptionGroupsByRestaurant error:',
      error,
    )
    return []
  }

  // Sort nested options by sort_order
  const groups = (data || []) as SupabaseOptionGroup[]
  return groups.map((group) => ({
    ...group,
    options: (group.options || [])
      .filter((opt) => opt.is_active)
      .sort((a, b) => a.sort_order - b.sort_order),
  }))
}

/**
 * Fetch option groups scoped to a specific product.
 * Returns groups where product_id matches OR product_id is null (global groups).
 */
export async function getOptionGroupsByProduct(
  restaurantId: string,
  productId: string,
): Promise<SupabaseOptionGroup[]> {
  const { data, error } = await supabase
    .from('option_groups')
    .select(
      `
      *,
      options:options(*)
    `,
    )
    .eq('restaurant_id', restaurantId)
    .eq('is_active', true)
    .or(`product_id.eq.${productId},product_id.is.null`)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('[restaurant-queries] getOptionGroupsByProduct error:', error)
    return []
  }

  const groups = (data || []) as SupabaseOptionGroup[]
  return groups.map((group) => ({
    ...group,
    options: (group.options || [])
      .filter((opt) => opt.is_active)
      .sort((a, b) => a.sort_order - b.sort_order),
  }))
}

/**
 * Fetch options for a specific option group.
 */
export async function getOptionsByGroup(
  groupId: string,
): Promise<SupabaseOption[]> {
  const { data, error } = await supabase
    .from('options')
    .select('*')
    .eq('option_group_id', groupId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('[restaurant-queries] getOptionsByGroup error:', error)
    return []
  }

  return (data || []) as SupabaseOption[]
}

// ─── Order Tracking (Read) ───────────────────────────────────

/**
 * Get order tracking info via RPC (public, domain-scoped).
 */
export async function getOrderTracking(
  domain: string,
  trackingToken: string,
): Promise<Record<string, unknown> | null> {
  const { data, error } = await supabase.rpc('get_order_tracking', {
    p_domain: domain,
    p_tracking_token: trackingToken,
  })

  if (error) {
    console.error('[restaurant-queries] getOrderTracking error:', error)
    return null
  }

  if (Array.isArray(data)) {
    return data.length > 0 ? data[0] : null
  }

  return data || null
}
